import { Component, Inject, Input, OnInit } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Certification, VerifiedCredentialIssuer, VerifiedCredentialStatusEnum } from '@queries/generated';
import { SwitchFade } from '@util/animations';
import { insertIf } from '@util/insert-if';
import { IWizard } from '@util/wizard';
import { WINDOW } from 'src/app/app.module';
import { BaseWizard } from '../base-wizard';
import { DialogToggleComponent } from '../dialog-toggle/dialog-toggle.component';

interface UserUpdateAttributes {
  firstName?: string;
  lastName?: string;
  email?: string;
}

enum VerifiedCredentialStepsEnum {
  ValueProposition,
  ApplicationHelp,
  TermsOfService,
  PresentationHelp,
  PersonCredential,
  CourseCredential,
  IssuerError,
  PlatformError
}

interface VerifiedCredentialWizard extends IWizard<VerifiedCredentialStepsEnum> {
  issuer?: Partial<VerifiedCredentialIssuer>;
  userAttributes?: UserUpdateAttributes;
}

@UntilDestroy()
@Component({
  selector: 'vnm-verifiable-credential-wizard',
  templateUrl: './verifiable-credential-wizard.component.html',
  styleUrls: ['./verifiable-credential-wizard.component.scss'],
  animations: [SwitchFade]
})
export class VerifiableCredentialWizardComponent extends BaseWizard implements OnInit {
  VerifiedCredentialStepsEnum = VerifiedCredentialStepsEnum;

  @Input() certification!: Certification;

  wizard: VerifiedCredentialWizard = {
    currentStep: 0,
    steps: [],
    running: false,
    errors: [],
    userAttributes: {}
  };

  constructor(
    private translateService: TranslateService,
    private dialogToggle: DialogToggleComponent,
    @Inject(WINDOW) private window: Window
  ) {
    super();
  }

  ngOnInit() {
    const {
      verifiedCredentialIssuer: issuer,
      verifiedCredentialStatus,
      userFirstName,
      userLastName
    } = this.certification;
    this.wizard.issuer = issuer;

    switch (verifiedCredentialStatus) {
      // Bad VC config...
      case VerifiedCredentialStatusEnum.NotVerifiedCredential: // not even configured for VC, how did you get here?
      case VerifiedCredentialStatusEnum.IssuerMissing: // no issuer, how did you get here?
      case VerifiedCredentialStatusEnum.UserMismatch: // you can't export VCs for other people, how did you get here?
      // Bad Personal Credential details..
      case VerifiedCredentialStatusEnum.NameMissing: // name completely missing from the person credential we have
        this.wizard.steps = [
          {
            label: this.translateService.instant('verified-credential-wizard.steps.issuer-error.title'),
            automationId: VerifiedCredentialStepsEnum.IssuerError
          }
        ];
        return;
      // Platform issues...
      case VerifiedCredentialStatusEnum.FirstNameMismatch:
        this.wizard.userAttributes.firstName = userFirstName;
        break;
      case VerifiedCredentialStatusEnum.LastNameMismatch: // person credential name doesn't match the user's name
        this.wizard.userAttributes.lastName = userLastName;
        break;
      case VerifiedCredentialStatusEnum.EmailMissing: // you don't have an email
        this.wizard.userAttributes.email = '';
        break;
      case VerifiedCredentialStatusEnum.NameMismatch: // name on cert doesn't match name on user
        break;
    }

    const { verificationRequired, verified } = issuer.currentUserVerification;
    const requirePresentation = !verified && verificationRequired;
    const ok =
      verifiedCredentialStatus == VerifiedCredentialStatusEnum.Ok || // everything is fine
      (requirePresentation && verifiedCredentialStatus == VerifiedCredentialStatusEnum.CredentialMissing); // presentation required, but missing, we'll get in via the wizard

    if (!ok) {
      // Platform error, use the attributes from the switch above
      this.wizard.steps = [
        {
          label: this.translateService.instant('verified-credential-wizard.steps.platform-error.title'),
          automationId: VerifiedCredentialStepsEnum.PlatformError
        }
      ];
      return;
    }

    const showTermsOfService = issuer.termsOfService.some((tos) => !tos.agreed);

    this.wizard.steps = [
      ...insertIf(issuer?.valuePropositionHtml, {
        label: this.translateService.instant('verified-credential-wizard.steps.value-proposition.title'),
        automationId: VerifiedCredentialStepsEnum.ValueProposition
      }),
      ...insertIf(issuer?.helpHtml, {
        label: this.translateService.instant('verified-credential-wizard.steps.application-help.title'),
        automationId: VerifiedCredentialStepsEnum.ApplicationHelp
      }),
      ...insertIf(showTermsOfService, {
        label: this.translateService.instant('verified-credential-wizard.steps.application-tos.title'),
        automationId: VerifiedCredentialStepsEnum.TermsOfService
      }),
      ...insertIf(requirePresentation && issuer?.requiredPresentationHelpHtml, {
        label: this.translateService.instant('verified-credential-wizard.steps.presentation-help.title'),
        automationId: VerifiedCredentialStepsEnum.PresentationHelp
      }),
      ...insertIf(requirePresentation, {
        label: this.translateService.instant('verified-credential-wizard.steps.person-credential.title'),
        automationId: VerifiedCredentialStepsEnum.PersonCredential
      }),
      {
        label: this.translateService.instant('verified-credential-wizard.steps.course-credential.title'),
        automationId: VerifiedCredentialStepsEnum.CourseCredential
      }
    ];
  }

  onActivationChange(canDeactivate: boolean) {
    this.wizard.running = !canDeactivate;
  }

  onDismiss() {
    this.dialogToggle?.close();
  }

  onOpenAppLink(url: string) {
    this.window.open(url, '_blank');
  }

  onContentClick(event: PointerEvent) {
    const { target } = event;

    if (target instanceof HTMLAnchorElement) {
      event.preventDefault();
      this.window.open(target.href, '_blank');
    }
  }

  gotoEnd() {
    this.wizard.currentStep = this.wizard.steps.length - 1;
  }

  override nextStep() {
    this.wizard.running = false;
    super.nextStep();
  }

  override prevStep() {
    this.wizard.running = false;
    super.prevStep();
  }
}
